import React, { useState } from 'react';
import { Alert, Button, Collapse, DatePicker, Dropdown, Typography } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import type { WorkflowInvoice } from '../../../types/workflowInvoice';
import type { WorkflowInvoiceIssue } from '../workflowInvoiceValidation';
import '../../../components/orders/OrderWizardShell.css';
import './InvoicePurchaseFlow.css';

interface Props {
  draft: WorkflowInvoice;
  rateNotice?: string;
  step: number;
  setStep: (step: number) => void;
  busy: boolean;
  extracting: boolean;
  conflict: boolean;
  dirty: boolean;
  issues: WorkflowInvoiceIssue[];
  editor: React.ReactNode;
  orders: React.ReactNode;
  receipt: React.ReactNode;
  canReceive: boolean;
  showApply: boolean;
  onApply: () => void;
  onUpload: () => void;
  onExtract: () => void;
  onSave: () => void;
  onContinue: () => Promise<void>;
  onApprove: () => void;
  onClose: () => void;
  onPeriod: (period: string | null) => void;
  onDownload: (sha: string) => void;
  onDelete: () => void;
  onReload: () => void;
}

/** Presentation only. Draft, OCR, valuation and approval remain owned by InvoiceDraftDetail. */
export default function InvoicePurchaseFlow(props: Props) {
  const { t } = useTranslation(['invoices', 'common']);
  const [showIssues, setShowIssues] = useState(false);
  const { draft, step, busy, extracting, conflict } = props;
  const locked = busy || extracting || conflict;
  const filePresent = Boolean(draft.sourceFileName || draft.sourceFiles?.length);
  const text = (key: string) => t(`invoices:workflow.flow.${key}`);
  return <div className="order-wizard invoice-purchase-flow" data-testid="invoice-purchase-flow">
    <nav className="order-wizard__steps invoice-purchase-flow__steps" aria-label={text('progress')}>
      {['file', 'review', 'receipt'].map((key, index) => <div key={key} className={`order-wizard__step ${step === index ? 'is-active' : step > index ? 'is-complete' : ''}`} aria-current={step === index ? 'step' : undefined}>
        <span className="order-wizard__step-index">{index + 1}</span><span className="order-wizard__step-copy">{text(key)}</span>
      </div>)}
    </nav>
    <div className="invoice-purchase-flow__body">
      {conflict && <Alert type="error" showIcon message={t('invoices:workflow.api.versionConflict')} description={t('invoices:workflow.page.conflictRetained')} action={<Button onClick={props.onReload}>{t('common:refresh')}</Button>} />}
      <section hidden={step !== 0} className="invoice-purchase-flow__intake">
        <UploadOutlined className="invoice-purchase-flow__file-icon" />
        <h2>{text('uploadTitle')}</h2>
        <p>{text('uploadHint')}</p>
        <Button type="primary" icon={<UploadOutlined />} onClick={props.onUpload} loading={busy} disabled={extracting || conflict}>{text('upload')}</Button>
        <Button type="link" onClick={() => props.setStep(1)} disabled={locked}>{text('manual')}</Button>
      </section>
      <section hidden={step !== 1}>
        <div className="invoice-purchase-flow__review-heading">
          <h2>{text('reviewTitle')}</h2>
          <Dropdown disabled={locked} menu={{ items: [{key: 'upload', label: text('addFile')}, ...(filePresent ? [{key: 'extract', label: text('retry')}] : []), ...(draft.id ? [{key: 'remove', label: t('invoices:workflow.page.deleteDraft'), danger: true}] : [])], onClick: ({key}) => { if (key === 'upload') props.onUpload(); if (key === 'extract') props.onExtract(); if (key === 'remove') props.onDelete(); } }}><Button type="text" aria-label={text('options')}>···</Button></Dropdown>
        </div>
        {extracting ? <Alert type="info" showIcon message={text('reading')} /> : draft.extractionStatus === 'FAILED'
          ? <Alert type="warning" showIcon message={text('failed')} />
          : draft.extractionStatus === 'SUCCESS' && <Typography.Paragraph type="secondary">{text('readSuccess')}</Typography.Paragraph>}
        {props.showApply && <Alert type="info" message={text('applyHint')} action={<Button disabled={locked} onClick={props.onApply}>{t('invoices:workflow.page.applyExtraction')}</Button>} />}
        {!!draft.sourceFiles?.length && <div className="invoice-purchase-flow__files">{draft.sourceFiles.map(file => <Button type="link" key={file.sha256} onClick={() => props.onDownload(file.sha256)}>{file.name}</Button>)}</div>}
        {showIssues && props.issues.length > 0 && <Alert type="warning" message={text('missing')} description={<ul>{props.issues.map(issue => <li key={`${issue.field}-${issue.key}`}>{t(issue.key)}</li>)}</ul>} />}
        {props.rateNotice && <p className="invoice-purchase-flow__rate" role="status">{props.rateNotice}</p>}
        {props.editor}
        <Collapse ghost items={[{ key: 'orders', label: text('orders'), forceRender: true, children: props.orders }]} />
      </section>
      <section hidden={step !== 2}>
        <h2>{text('receiptTitle')}</h2>
        <p className="invoice-purchase-flow__hint">{text('receiptHint')}</p>
        {props.receipt}
        <label className="invoice-purchase-flow__period">
          <span>{t('invoices:workflow.page.recognitionMonth')}</span>
          <DatePicker picker="month" aria-label={t('invoices:workflow.page.recognitionMonth')} value={draft.settlementPeriod ? dayjs(`${draft.settlementPeriod}-01`) : null} disabled={locked} onChange={date => props.onPeriod(date?.format('YYYY-MM') ?? null)} />
        </label>
      </section>
    </div>
    <footer className="order-wizard__actions invoice-purchase-flow__actions">
      <Button onClick={props.onClose} disabled={busy || extracting}>{t('common:close')}</Button>
      {props.dirty && <span className="invoice-purchase-flow__hint">{t('invoices:workflow.summary.unsaved')}</span>}
      <div className="invoice-purchase-flow__action-right">
        {step > 0 && <Button disabled={locked} onClick={() => props.setStep(step - 1)}>{t('common:back')}</Button>}
        {step === 1 && <Button disabled={locked} onClick={props.onSave}>{t('invoices:workflow.page.saveDraft')}</Button>}
        {step === 1 && <Button type="primary" loading={busy} disabled={extracting || conflict} onClick={() => { setShowIssues(true); if (!props.issues.length) void props.onContinue(); }}>{text('continue')}</Button>}
        {step === 2 && <Button type="primary" loading={busy} disabled={extracting || conflict || props.dirty || !props.canReceive || !draft.settlementPeriod} onClick={props.onApprove}>{text('receive')}</Button>}
      </div>
    </footer>
  </div>;
}
